import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, logout, selectUserProfileId } from '@hwm/ui-lib';
import { makeStyles, Menu, Theme } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import PersonIcon from '@material-ui/icons/Person';
import { useUserDetailsDataSource } from './userDetailsDataSource';
import { environment } from '@root/environment';
import { useAppDispatch, useAppSelector } from '@root/store/store';
import { selectedAccStateSelector } from '@root/store/library-stores/selectors';
import { accountsReducer } from '@root/store/library-stores/accounts';
import { isNil } from 'lodash';
import { styleConstants } from '@styles/constants';

export const useStyles = makeStyles((theme: Theme) => ({
  userDetailsMenuWrapper: {
    top: '66px !important',
    left: 'unset !important',
    right: 8,
  },
  userDetailsMenuListWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
    padding: '24px',
    color: styleConstants.colors.grey,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '16px',
    '& > *:not(:last-child)': {
      marginBottom: '8px',
    },
  },
  version: {
    fontSize: '12px',
    textAlign: 'right',
    width: '100%',
    color: '#B6BCCE',
    borderTop: '1px solid #B6BCCE',
    paddingTop: '7px',
    marginTop: '13px',
  },
}));

export const UserDetails: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const userDetailsRef = useRef<HTMLDivElement>(null);
  const [openUserDetails, setOpenUserDetails] = useState<boolean>(false);

  const { userMainAccount } = useAppSelector(selectedAccStateSelector);
  const userId: number = useAppSelector(selectUserProfileId);

  const {
    userAccessProfile,
    getUserAccessProfile,
    loading,
    error,
    handleSubscriptionForAccount,
    isAlarmSubscribedForAccount,
  } = useUserDetailsDataSource();

  useEffect(() => {
    if (isNil(userId)) return;
    getUserAccessProfile(userId);
  }, [userId, getUserAccessProfile]);

  const userRole = useMemo(
    () =>
      userAccessProfile
        ? userAccessProfile?.roles?.find((role) => role.applicationName === environment.appName)?.roleName || null
        : '',
    [userAccessProfile]
  );

  useEffect(() => {
    if (!userAccessProfile || loading || !userId || error) return;

    dispatch(accountsReducer.slice.actions.setUserRole(userRole));
  }, [dispatch, userAccessProfile, userId, userRole, loading, error]);

  return (
    <div ref={userDetailsRef}>
      <Button
        variant="outlined"
        onClick={() => setOpenUserDetails(!openUserDetails)}
        style={{ fontSize: '16px' }}
        endIcon={openUserDetails ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        startIcon={<PersonIcon />}
      >
        {userMainAccount?.userName}
      </Button>
      <Menu
        open={openUserDetails}
        anchorEl={userDetailsRef?.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClick={() => setOpenUserDetails(false)}
        classes={{
          paper: classes.userDetailsMenuWrapper,
          list: classes.userDetailsMenuListWrapper,
        }}
      >
        <div>Parent Account: {userMainAccount?.name}</div>
        <div>User Level: {loading ? '' : userRole}</div>
        <Button variant="outlined" onClick={handleSubscriptionForAccount} style={{ fontSize: '16px' }}>
          {isAlarmSubscribedForAccount ? 'Unsubscribe from' : 'Subscribe to'} all sites notification
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            logout({
              sessionCookieDomain: environment.sessionCookieDomain,
              sessionCookieIsSecure: environment.sessionCookieSecure,
              sessionIdleTimeout: environment.sessionCookieIdleTimeout,
            });
          }}
          style={{ fontSize: '16px' }}
        >
          Log out
        </Button>
        <div className={classes.version}>Version: {environment.version}</div>
      </Menu>
    </div>
  );
};
